import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { routePath } from "@/i18n/routes";
import { CAREERS_SALES } from "@/i18n/jobs";
import { Kicker, PageHero, Section, CardGrid, VerticalList } from "@/components/Primitives";
import { Button } from "@/components/ui/button";

export default function Careers() {
  const { t, lang } = useI18n();
  const sales = CAREERS_SALES[lang] || CAREERS_SALES.fr;
  const applyTo = routePath("apply", lang);
  return (
    <div data-testid="careers-page">
      <Seo pageKey="careers" />
      <PageHero kicker={t("careers.kicker")} title={sales.h1} lead={sales.intro} />

      <Section>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Button asChild size="lg" data-testid="careers-apply-cta" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
            <Link to={applyTo}>
              {sales.applyCta} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" data-testid="careers-opportunities-cta" className="rounded-full px-8">
            <Link to={routePath("opportunities", lang)}>{t("cta.discover")}</Link>
          </Button>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-2">
          <div data-testid="careers-sales-block">
            <h2 className="font-display text-3xl font-light leading-tight sm:text-4xl">{sales.salesTitle}</h2>
            {sales.salesBody.map((paragraph) => (
              <p key={paragraph} className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
                {paragraph}
              </p>
            ))}
          </div>
          <div data-testid="careers-why-block">
            <h2 className="font-display text-3xl font-light leading-tight sm:text-4xl">{sales.whyTitle}</h2>
            <ul className="mt-6 space-y-4">
              {sales.whyPoints.map((point) => (
                <li key={point} className="flex gap-4 text-base leading-relaxed text-muted-foreground">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-accent" aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16">
          <Kicker>{t("careers.howKicker")}</Kicker>
          <CardGrid items={t("careers.how")} testIdPrefix="careers-step" />
        </div>
        <p data-testid="careers-no-fake" className="mt-12 max-w-3xl border-l-2 border-accent pl-6 text-sm text-muted-foreground">
          {t("careers.noFake")}
        </p>
      </Section>

      <Section className="bg-secondary/40">
        <div data-testid="careers-job-offer" className="max-w-3xl">
          <h2 className="font-display text-3xl font-light leading-tight sm:text-4xl">{sales.offerTitle}</h2>
          <dl className="mt-10 divide-y divide-border border-y border-border">
            {sales.offerRows.map(([label, value]) => (
              <div key={label} className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-3 sm:gap-6">
                <dt className="cs-kicker">{label}</dt>
                <dd className="text-base leading-relaxed sm:col-span-2">{value}</dd>
              </div>
            ))}
          </dl>
          <Button asChild size="lg" data-testid="careers-offer-apply-cta" className="mt-10 rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
            <Link to={applyTo}>
              {sales.applyCta} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </Section>

      <Section>
        <div data-testid="careers-faq" className="max-w-3xl">
          <h2 className="font-display text-3xl font-light leading-tight sm:text-4xl">{sales.faqTitle}</h2>
          <div className="mt-10 space-y-8">
            {sales.faq.map((item, index) => (
              <article key={item.q} data-testid={`careers-faq-${index}`}>
                <h3 className="font-display text-xl sm:text-2xl">{item.q}</h3>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">{item.a}</p>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-secondary/40">
        <Kicker>{t("opportunities.categoriesTitle")}</Kicker>
        <VerticalList testId="careers-verticals" />
      </Section>
    </div>
  );
}
